import type {
  ConversionsEventMeta,
  ConversionsFunction,
  ConversionsServerStatus,
  ConversionsTaggedEvent,
} from '@dfh-corp-sites/www-api'
import { createSignal } from '@react-rxjs/utils'
import {
  catchError,
  concatMap,
  distinctUntilChanged,
  EMPTY,
  map,
  ObservedValueOf,
  of,
  retry,
  switchMap,
  tap,
  timer,
} from 'rxjs'
import { fromFetch } from 'rxjs/fetch'

import { logAndCaptureException } from '../../utils/errorTools'
import { MappedOmit } from '../../utils/omit'
import { coerceCaughtToLeft } from '../../utils/rx/errors'
import { isUserCategory, UserCategory, userClaimIds } from '../../utils/userClaims'
import { latestAuthState$ } from '../auth/state'
import { fetchResponseToJsonResult, headers } from '../fetch'

export type { ConversionsEventMeta } from '@dfh-corp-sites/www-api'

/**
 * Create input signal for Meta/FB conversions events
 */
export const [conversionsEventMetaChange$, setConversionsEventMeta] =
  createSignal<
    MappedOmit<ConversionsEventMeta, 'action_source' | 'event_time' | 'opt_out' | 'user_data'>
  >()

/**
 * Calculate SHA-256 hash of UTF-8 encoded string and return hash encoded as hex string
 */
const sha256Hex = async (email: string) =>
  Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(email))))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')

const randomId = (): string =>
  window
    ? window.crypto.getRandomValues(new Uint32Array(1))[0].toString(16)
    : Math.random().toString(36).substring(2, 12)

/**
 * Pick specific fields from event `custom_data` common to all events that we add here before
 * sending the event to the server.
 */
type CommonCustomData = Pick<ConversionsEventMeta['custom_data'], 'customer_category'>

/**
 * Assign common fields to event `custom_data`. This avoids conditional logic to check each
 * event type in the discriminated union prior to spreading and adding common fields.
 */
const assignCommonCustomData = (
  event: ObservedValueOf<typeof conversionsEventMetaChange$>,
  commonData: CommonCustomData,
): ObservedValueOf<typeof conversionsEventMetaChange$> =>
  Object.assign({}, event, {
    custom_data: { ...event.custom_data, ...commonData },
  })

/**
 * Merge conversions events with user data from auth state and add common fields
 */
const conversionsTaggedEvent$ = latestAuthState$.pipe(
  map(({ user }) => user),
  distinctUntilChanged(),
  switchMap(
    async (
      user,
    ): Promise<{
      eventIdPrefix?: string
      userData: ConversionsEventMeta['user_data']
      userCategory?: UserCategory
    }> =>
      user
        ? Promise.all([
            user.email && sha256Hex(user.email),
            // hash Auth0 user id and current timestamp and use as prefix for event id
            user.sub && sha256Hex(`${user.sub}${Date.now()}`),
            user.sub && sha256Hex(user.sub),
          ]).then(([em, eventIdPrefix, external_id]) => {
            const userCategory = user[userClaimIds.category] as unknown
            return {
              ...(eventIdPrefix && { eventIdPrefix }),
              userData: {
                ...(em && { em }),
                ...(external_id && { external_id }),
              },
              ...(isUserCategory(userCategory) && { userCategory }),
            }
          })
        : { userData: {} },
  ),
  switchMap(({ eventIdPrefix, userData, userCategory }) =>
    conversionsEventMetaChange$.pipe(
      map(
        (event): ConversionsTaggedEvent => ({
          _tag: 'meta',
          event: {
            // use Object.assign() to add common `custom_data` fields to event
            ...assignCommonCustomData(event, {
              ...(userCategory && {
                customer_category: userCategory,
              }),
            }),
            action_source: 'website' as const,
            // generate event id if prefix is available from user data
            ...(eventIdPrefix && { event_id: `${eventIdPrefix}-${randomId()}` }),
            ...(typeof window !== 'undefined' && { event_source_url: window.location.href }),
            // current time in seconds
            event_time: Math.floor(Date.now() / 1000).toString(),
            user_data: userData,
          },
        }),
      ),
    ),
  ),
)

const mkPrepareRequest = (
  events: Array<ConversionsTaggedEvent>,
): ConversionsFunction<'prepare'>['request'] => ({
  f: 'prepare',
  ma: events,
})

const mkSendRequest = (
  events: Array<ConversionsTaggedEvent>,
): ConversionsFunction<'send'>['request'] => ({
  f: 'send',
  ma: events,
})

const retryWithBackoff = <T>() =>
  retry<T>({
    count: 3,
    delay: (_, count) =>
      // retry with exponential backoff and max of 60 seconds
      timer(Math.min(60000, 2 ^ (count * 1000))),
  })

// create immediate subscription to observable
if (typeof window !== 'undefined') {
  conversionsTaggedEvent$
    .pipe(
      concatMap((event) =>
        // check conversions status - is it supported?
        fromFetch('/api/conversions', { method: 'GET' }).pipe(
          switchMap((res) => fetchResponseToJsonResult<ConversionsServerStatus>(res)),
          tap((result) => {
            if (result._tag === 'Left') {
              logAndCaptureException(result.error)
            }
          }),
          // retry network errors
          retryWithBackoff(),
          catchError(coerceCaughtToLeft),
          switchMap((result) =>
            // if conversions is supported, return the event payload
            result._tag === 'Right' && result.data.supportedProviders.includes(event._tag)
              ? of(event)
              : EMPTY,
          ),
        ),
      ),
      // prepare event
      concatMap((event) =>
        fromFetch('/api/conversions', {
          method: 'POST',
          body: JSON.stringify(
            mkPrepareRequest([
              event,
              ...(event.event.event_name === 'Purchase' &&
              event.event.custom_data.customer_category === 'patient'
                ? [
                    {
                      ...event,
                      event: {
                        ...event.event,
                        event_name: 'PatientPurchase' as const,
                        custom_data: {
                          ...event.event.custom_data,
                          customer_category: 'patient' as const,
                        },
                      },
                    },
                  ]
                : event.event.event_name === 'Purchase' &&
                    event.event.custom_data.customer_category === 'practitioner'
                  ? [
                      {
                        ...event,
                        event: {
                          ...event.event,
                          event_name: 'PractitionerPurchase' as const,
                          custom_data: {
                            ...event.event.custom_data,
                            customer_category: 'practitioner' as const,
                          },
                        },
                      },
                    ]
                  : []),
            ]),
          ),
          headers: {
            ...headers.acceptJson,
            ...headers.contentTypeJson,
          },
        }).pipe(
          switchMap((res) =>
            fetchResponseToJsonResult<ConversionsFunction<'prepare'>['response']>(res),
          ),
          tap((result) => {
            if (result._tag === 'Left') {
              logAndCaptureException(result.error)
            }
          }),
          // retry network errors
          retryWithBackoff(),
          catchError(coerceCaughtToLeft),
          switchMap((result) =>
            // if prepare was successful, return transformed event(s) if any remain
            result._tag === 'Right' && result.data.ma.length ? of(result.data.ma) : EMPTY,
          ),
        ),
      ),
      // send event
      concatMap((events) =>
        fromFetch('/api/conversions', {
          method: 'POST',
          body: JSON.stringify(mkSendRequest(events)),
          headers: {
            ...headers.acceptJson,
            ...headers.contentTypeJson,
          },
        }).pipe(
          switchMap(fetchResponseToJsonResult),
          tap((result) => {
            if (result._tag === 'Left') {
              logAndCaptureException(result.error)
            }
          }),
          // retry network errors
          retryWithBackoff(),
          catchError(coerceCaughtToLeft),
        ),
      ),
    )
    .subscribe()
}
